// Single source of truth for who may see and open what.
//
// The router is the enforcement point (ProtectedRoute / AdminOrOwnerRoute /
// OwnerRoute) and firestore.rules is the real boundary. These helpers exist so
// the Sidebar offers exactly the pages the router will actually open: the nav
// previously gated Returns and User Admin behind owner-only while the router
// allowed admins, leaving admins with reachable pages and no link to them.
//
// Access tiers, matching App.js:
//   admin OR owner          -> /summary, /returns, /paymentRecord
//   reconciliation OR owner -> /reconciliation
//   owner only              -> /paymentHistory, /deliveryDashboard,
//                              /userpackagesummary, /userpackagedetail,
//                              /useradmin
//
// OWNER IS A SUPERSET: an owner can see every page in the app. Every capability
// predicate below folds owner in, so a new role grants access to its own pages
// without anyone having to remember to re-grant the owner.

// Profiles carry roles two ways: the current `roles: ['admin', ...]` array, and
// some older records a single `role: 'admin'` string. OwnerRoute already
// tolerated both; read every profile the same way so a person's access does not
// depend on which guard they happen to hit.
//
// Note: firestore.rules only ever checks the `roles` ARRAY. A profile carrying
// just `role: 'admin'` passes these client checks but is still denied by the
// server, so such a profile needs a real `roles` array to actually work.
export const rolesOf = (userData) => {
  if (!userData) return [];
  const list = Array.isArray(userData.roles) ? userData.roles : [];
  const single = typeof userData.role === 'string' ? [userData.role] : [];
  return [...new Set([...list, ...single].map(role => String(role).toLowerCase()))];
};

export const isOwner = (roles) => roles.includes('owner');
export const isAdmin = (roles) => roles.includes('admin');
export const isDriver = (roles) => roles.includes('driver');
export const isReconciler = (roles) => roles.includes('reconciliation');

// --- Capability checks. Each one folds in owner, per the superset rule above. ---

// Admins are not owners, but everything an admin may open, an owner may too.
export const isAdminOrOwner = (roles) => isAdmin(roles) || isOwner(roles);

// Payment reconciliation: reading packages/payments and flipping the verified
// flag on a payment. Deliberately NOT granted to admin — it is a finance task,
// so it takes either the dedicated role or ownership.
export const canReconcile = (roles) => isReconciler(roles) || isOwner(roles);

// Highest-ranking role, for display only.
export const roleLabel = (roles) => {
  if (isOwner(roles)) return 'Owner';
  if (isAdmin(roles)) return 'Admin';
  if (isReconciler(roles)) return 'Reconciliation';
  if (isDriver(roles)) return 'Driver';
  return 'Staff';
};
